import { LANGUAGE } from "@monorepo/constants";
import { LanguagesEnum } from "@monorepo/types";

export const convertNumberToCurrency = (value: number | string): string => {
  switch (LANGUAGE) {
    case LanguagesEnum.PL:
      return new Intl.NumberFormat("pl-PL", {
        style: "currency",
        currency: "PLN",
        currencyDisplay: "code",
      }).format(+value);
    default:
      return new Intl.NumberFormat("lt-LT", {
        style: "currency",
        currency: "EUR",
      }).format(+value);
  }
};

export const convertToCurrencyCodeFormat = (value: number | string): string => {
  const props: Intl.NumberFormatOptions = {
    style: "currency",
    minimumFractionDigits: Number(value) % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
    currencyDisplay: "code",
  };

  switch (LANGUAGE) {
    case LanguagesEnum.PL:
      return new Intl.NumberFormat("pl-PL", {
        currency: "PLN",
        ...props,
      }).format(+value);
    default:
      return new Intl.NumberFormat("lt-LT", {
        currency: "EUR",
        ...props,
      }).format(+value);
  }
};

export const latinizeString = (input: string) => {
  /**
   * converts ąčęį to acei etc.
   */
  return input.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

export const padMonth = (number: number | string): string => {
  return String("0" + (Number(number) || 1)).slice(-2);
};

export const padDay = padMonth;

export const isLastYearMonth = (isHistorical: boolean = false) => Number(new Date().getMonth()) === 0 && isHistorical;

export const getMonth = (isHistorical: boolean = false) => {
  if (isLastYearMonth(isHistorical)) {
    return "months.previousMonth";
  }

  return `months.${padMonth(Number(new Date().getMonth() + (isHistorical ? 0 : 1)))}`;
};

export const getCountryPhoneCode = () => {
  switch (LANGUAGE) {
    case LanguagesEnum.PL:
      return "48";
    default:
      return "370";
  }
};

export const formatPhoneView = (value: string) => {
  switch (LANGUAGE) {
    case LanguagesEnum.PL:
      return value
        .replace(/\W/gi, "")
        .replace(/(\d{3})(\d{3})/, "$1 $2 ") //inserts space after each three chars
        .replace(/ $/g, "");
    default:
      return value
        .replace(/\W/gi, "") //\w, \W: ANY ONE word/non-word character. For ASCII, word characters are [a-zA-Z0-9_]; g- global, i - doesn't matter uppercase or lowercase
        .replace(/(\d{3})/, "$1 ") //inserts space after first three chars
        .replace(/ $/g, "");
  }
};

export const getCountryPhoneCodeWithPlus = () => {
  return "+" + getCountryPhoneCode();
};

export const getPhonePlaceholder = () => {
  switch (LANGUAGE) {
    case LanguagesEnum.PL:
      return "XXX XXX XXX";
    default:
      return "6XX XXXXX";
  }
};

export const phonesWithEmptySpacesLengths = {
  lt: 9,
  pl: 11,
};

export const getPhoneHiddenPartByCountry = () => {
  switch (LANGUAGE) {
    case LanguagesEnum.PL:
      return "****";
    default:
      return "***";
  }
};

export const getPhoneBeggingReplaceByCountry = () => {
  switch (LANGUAGE) {
    case LanguagesEnum.PL:
      return /^(48)/;
    default:
      return /^(370)/;
  }
};

export const formatPhone = (phone: string): string => {
  return phone.replace(/ /g, "");
};

export const getHiddenPhone = (phone: string): string => {
  return (
    getCountryPhoneCodeWithPlus() +
    `${phone.slice(0, 3) + getPhoneHiddenPartByCountry() + phone.slice(phone.length - 2, phone.length)}`
  );
};

// for displaying
export const formatIbanView = (value: string) => {
  switch (LANGUAGE) {
    case LanguagesEnum.PL:
      return value
        .replace(/^(pl)/i, "PL")
        .replace(/^(?!(PL)(?!pl))/, "PL")
        .replace(/\W/gi, "")
        .replace(/([0-9]{2})/, "$1 ")
        .replace(/([0-9]{4})/g, "$1 ")
        .replace(/ $/g, "");
    default:
      return value
        .replace(/^(lt)/i, "LT")
        .replace(/^(?!(LT)(?!lt))/, "LT")
        .replace(/\W/gi, "")
        .replace(/([^0-9]{2})/, "$1 ")
        .replace(/([0-9]{2})/, "$1 ")
        .replace(/([0-9]{4})/g, "$1 ")
        .replace(/ $/g, "");
  }
};
// for sending to backend (contains no spaces)
export const formatIbanModel = (value: string) => {
  switch (LANGUAGE) {
    case LanguagesEnum.PL:
      return value
        .replace(/^(pl)/i, "PL")
        .replace(/^(?!(PL)(?!pl))/, "PL")
        .replace(/\W/gi, "")
        .replace(/([^0-9]{2})/, "$1 ")
        .replace(/([0-9]{2})/, "$1 ")
        .replace(/([0-9]{4})/g, "$1 ")
        .replace(/ /g, "");
    default:
      return value
        .replace(/^(lt)/i, "LT")
        .replace(/^(?!(LT)(?!lt))/, "LT")
        .replace(/\W/gi, "")
        .replace(/([^0-9]{2})/, "$1 ")
        .replace(/([0-9]{2})/, "$1 ")
        .replace(/([0-9]{4})/g, "$1 ")
        .replace(/ /g, "");
  }
};

export const finalIbanRegex = (value: string) => {
  switch (LANGUAGE) {
    case LanguagesEnum.PL:
      return /^PL(\d){26}$/.test(value); //26 - length of iban numeric values
    default: {
      return /^LT(\d){18}$/.test(value); //18 - length of iban numeric values
    }
  }
};

export const tooLongIbanRegex = (value: string) => {
  switch (LANGUAGE) {
    case LanguagesEnum.PL:
      return /^(P?L?)?( ?\d){0,27} ?$/.test(value); //26 - length of iban numeric values + 1
    default:
      return /^(L?T?)?( ?\d){0,19} ?$/.test(value); //18 - length of iban numeric values + 1
  }
};

export const ibanMatchesSpacingRegex = (value: string) => {
  switch (LANGUAGE) {
    case LanguagesEnum.PL:
      return /^(P?L?)?( ?\d){0,26} ?$/i.test(value); //26 - length of iban numeric values
    default:
      return /^(L?T?)?( ?\d){0,18} ?$/i.test(value); //18 - length of iban numeric values
  }
};

export const getIbanViewLength = () => {
  switch (LANGUAGE) {
    case LanguagesEnum.PL:
      return 34; // length of whole iban (incl. spaces, numeric, text values)
    default:
      return 25; // length of whole iban (incl. spaces, numeric, text values)
  }
};

export const getISO = () => {
  switch (LANGUAGE) {
    case LanguagesEnum.PL:
      return "PL";
    default:
      return "LT";
  }
};
